/**
 * # API Responses
 *
 * ## Endpoint: `${backendHost}${backendSignupUrl}`
 *
 * This endpoint is used for submitting signup data. The response can vary depending on the status code and the response body.
 *
 * ### Response Cases
 *
 * 1. **Status Code: 200 (OK), Response Body: `{ redirect_to: 'some_url' }`**
 *    - Description: This response indicates a successful signup. The `redirect_to` property in the response body contains
 * the URL to which the client should be redirected.
 *
 * 2. **Status Code: 400 (Bad Request), Response Body: `{ error_messages: ['error_message_1', 'error_message_2', ...] }`**
 *    - Description: This response indicates an error during the signup process. The `error_messages` array in the response
 * body contains a list of error messages.
 *
 * 3. **Status Code: 400 (Bad Request), Response Body: `{ status: 400, error: 'Bad request' }`**
 *    - Description: This response indicates an unhandled generic error during the signup process.
 *
 * ## Endpoint: `${backendHost}${backendLoginUrl}`
 *
 * This endpoint is used for submitting login data. The response can vary depending on the status code and the response body.
 *
 * ### Response Cases
 *
 * 1. **Status Code: 200 (OK), Response Body: `{ redirect_to: 'some_url' }`**
 *    - Description: This response indicates a successful login. The `redirect_to` property in the response body contains
 * the URL to which the client should be redirected.
 *
 * 2. **Status Code: 200 (OK), Response Body: `{ authentication_required: true, legacy_2fa?: true }`**
 *    - Description: This response indicates a successful login, but additional check (e.g., verification code) is
 * required. The optional `legacy_2fa` flag signals which 2FA experience to show:
 *      - `legacy_2fa: true` -> existing email OTP flow (code emailed to the user).
 *      - field absent -> new authenticator (TOTP) flow, with the option to use a backup code.
 *    - See "3.4 No Client-side feature flag" in the Phase 2 design: the backend owns the `two_factor_v2` flag and
 * only omits `legacy_2fa` when the flag is on AND the user is enrolled in the new authenticator 2FA.
 *
 * 3. **Status Code: 401 (Unauthorized), Empty JSON Body**
 *    - Description: This response indicates a failed login attempt.
 *
 * 2FA verification endpoints (`/sessions/verify`, `/sessions/verify_backup`) live in `./2fa`.
 */

/* eslint-disable @typescript-eslint/naming-convention */
import { fetchWithCsrf } from 'utils/fetchWithCsrf';
import * as Sentry from '@sentry/react';
import { backendHost, backendSignupUrl, backendLoginUrl, apiHeaders } from 'utils/constants';
import { LoginData, SignupData } from 'context/types';
import { formDataSanitiser } from 'utils/formDataSanitiser';
import { reportUserDataMissing } from 'utils/adobeAnalytics/utils/utils';
import { trackFormInteraction } from 'utils/adobeAnalytics/trackFormInteraction';
import { getReturnToParam } from 'utils/getReturnToParam';
import { getUTMData } from 'Marketing/utils/cookies';

export type SignupResponse = {
    redirect_to?: string;
    error_messages?: string[];
    // Required for AA.
    user_id?: string;
    company_id?: string;
    company_name?: string;
};

/**
 * is_employee: indicates whether the logged in user is the owner of the company or not. The BE
 * does not consider company owners as "employees". A user is considered an employee only if the
 * owner has created an account for them via settings.
 */
export type LoginResponse = {
    redirect_to?: string;
    error?: boolean;
    isVerificationRequired?: boolean;
    /**
     * Signals the legacy email OTP flow when `true`. Derived from the backend's `legacy_2fa` response
     * field: `true` -> email OTP; absent or `false` -> new authenticator (TOTP) flow.
     */
    isLegacy2fa?: boolean;
    // Required for AA.
    user_id?: string;
    company_id?: string;
    company_name?: string;
    // Used for analytics identification
    is_employee?: boolean; // Note - an employee is not considered an owner of a company.
};

export const decamelizedSignupData = (data: SignupData) => {
    const {
        country,
        email,
        login,
        password,
        companyName,
        addressLine1,
        city,
        region,
        postCode,
        phone,
        marketingConsent,
        termsAndConditions,
        amazonOauthAccessToken,
        amazonOauthUid,
        timezone,
        referralCode,
        referralCompanyID,
        adobeECID,
        monthlyOrderVolume,
    } = data;

    const utmData = getUTMData();

    const result = {
        login,
        email,
        city,
        region,
        timezone,
        post_code: postCode,
        address_line_1: addressLine1,
        company_name: companyName,
        country,
        phone,
        terms_and_conditions: termsAndConditions,
        marketing_consent: marketingConsent,
        referral_code: referralCode,
        referral_company_id: referralCompanyID,
        adobe_ecid: adobeECID,
        monthly_order_volume: monthlyOrderVolume,
        ...utmData,
    };

    if (amazonOauthUid?.length && amazonOauthAccessToken?.length) {
        return {
            ...result,
            amazon_oauth_uid: amazonOauthUid,
            amazon_oauth_access_token: amazonOauthAccessToken,
        };
    }
    return { ...result, password };
};

export const submitSignupData = async (submitData: SignupData, prefix = '') => {
    const sanitisedData = formDataSanitiser(submitData);

    try {
        const response = await fetchWithCsrf(`${backendHost}${prefix}${backendSignupUrl}`, {
            method: 'POST',
            mode: backendHost?.length ? 'cors' : 'same-origin',
            headers: apiHeaders,
            body: JSON.stringify({ signup: decamelizedSignupData(sanitisedData) }),
        });

        const { redirect_to, error_messages, user_id, company_id }: SignupResponse =
            await response.json();

        if (redirect_to) {
            if (!!user_id && !!company_id) {
                if (!submitData.companyName) Sentry.captureMessage('AA: company name is missing');

                trackFormInteraction({
                    formName: 'account-setup-form',
                    interactionType: 'submit',
                    account: { companyID: company_id, companyName: submitData.companyName },
                    user: { id: user_id },
                });
            } else {
                reportUserDataMissing({ user_id, company_id, company_name: submitData.companyName });
            }

            global.location.assign(redirect_to);
        }

        if (error_messages) {
            return { error_messages };
        }
    } catch (e) {
        return { error_messages: [] };
    }
};

export const submitLogin = async (loginData: LoginData) => {
    // Get return_to from URL and include it in the login request
    const returnTo = getReturnToParam();
    const dataWithReturnTo = returnTo ? { ...loginData, return_to: returnTo } : loginData;
    const sanitisedData = formDataSanitiser(dataWithReturnTo);

    try {
        const response = await fetchWithCsrf(`${backendHost}${backendLoginUrl}`, {
            method: 'POST',
            mode: backendHost?.length ? 'cors' : 'same-origin',
            headers: apiHeaders,
            body: JSON.stringify(sanitisedData),
        });
        const {
            redirect_to,
            authentication_required,
            legacy_2fa,
            user_id,
            company_id,
            company_name,
            is_employee,
        } = await response.json();
        return {
            redirect_to,
            isVerificationRequired: authentication_required,
            // The backend omits `legacy_2fa` only when the new-2FA flag is on AND the user is enrolled in
            // the authenticator flow (see §3.4), so an absent field deliberately means the new flow.
            isLegacy2fa: authentication_required ? Boolean(legacy_2fa) : undefined,
            user_id,
            company_id,
            company_name,
            is_employee,
        };
    } catch (e) {
        /**
         * To prevent 'Information leak based on failed login attempts' we do not provide
         * any feedback which field caused the login failure (email, password or both)
         * BE returns 401 Unauthorized response status code and we show generic:
         * "Wrong credentials. Try again or click Forgot password to reset it."
         */
        return { error: true };
    }
};
